import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Check,
  CheckCircle2,
  Eye,
  FileSpreadsheet,
  Loader2,
  ReceiptEuro,
  Send,
  TriangleAlert,
  Upload,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useBillingRun } from '@/hooks/useBillingRun';
import { useCancelBillingRun } from '@/hooks/useCancelBillingRun';
import { useBillingRunProgress } from '@/hooks/useBillingRunProgress';
import { useImportBillingRun } from '@/hooks/useImportBillingRun';
import { usePublishBillingRun } from '@/hooks/usePublishBillingRun';
import {
  validateAnnexHeader,
  type AnnexKind,
} from '@/lib/billing-annex-validation';
import { MONTHS } from '@/lib/billing-format';
import { cn } from '@/lib/utils';
import type { BillingRunStatus, DetailRow } from '@octave/contracts';
import { formatDecimal } from '@/lib/format';
import { useBillingCenterPreview } from '@/hooks/useBillingCenterPreview';

const MAX_SIZE_MB = 50;
const XLSX_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];
const isXlsx = (file: File): boolean =>
  XLSX_TYPES.includes(file.type) || file.name.toLowerCase().endsWith('.xlsx');
const formatBytes = (bytes: number): string =>
  bytes < 1024 * 1024
    ? `${Math.round(bytes / 1024)} Ko`
    : `${(bytes / 1024 / 1024).toFixed(1)} Mo`;

// --- Status indicator (derived from the persisted run status) ----------------

const STATUS_STEPS: { label: string; statuses: BillingRunStatus[] }[] = [
  { label: 'Analysé', statuses: ['analyzed'] },
  { label: 'Publication', statuses: ['publishing'] },
  { label: 'Publié', statuses: ['published'] },
];
const STATUS_ORDER: BillingRunStatus[] = ['analyzed', 'publishing', 'published'];

export function StatusSteps({ status }: { status: BillingRunStatus }) {
  const currentIdx = STATUS_ORDER.indexOf(status);
  return (
    <div className="flex items-center gap-2">
      {STATUS_STEPS.map((step, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={step.label} className="flex items-center gap-2">
            <div
              className={cn(
                'flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold',
                done
                  ? 'bg-emerald-600 text-white'
                  : active
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-400'
              )}
            >
              {done ? <Check className="h-3.5 w-3.5" /> : i + 1}
            </div>
            <span
              className={cn(
                'text-sm',
                active
                  ? 'font-medium text-slate-900'
                  : done
                    ? 'text-slate-600'
                    : 'text-slate-400'
              )}
            >
              {step.label}
            </span>
            {i < STATUS_STEPS.length - 1 && (
              <div
                className={cn(
                  'h-px w-8',
                  done ? 'bg-emerald-600' : 'bg-slate-200'
                )}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

export function PanelLoader() {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500">
      <Loader2 className="h-4 w-4 animate-spin" /> Chargement…
    </div>
  );
}

function Stat({
  label,
  value,
  warning,
}: {
  label: string;
  value: number | string;
  warning?: boolean;
}) {
  return (
    <div>
      <p className="text-xs text-slate-500">{label}</p>
      <p
        className={cn(
          'mt-0.5 flex items-center gap-1 text-lg font-semibold text-slate-900',
          warning && 'text-amber-600'
        )}
      >
        {warning && <TriangleAlert className="h-4 w-4" />}
        {value}
      </p>
    </div>
  );
}

// --- Dépôt (new import) ------------------------------------------------------

interface AnnexDropzoneProps {
  label: string;
  hint: string;
  kind: AnnexKind;
  files: File[];
  disabled: boolean;
  onFiles: (files: File[]) => void;
}

function AnnexDropzone({
  label,
  hint,
  kind,
  files,
  disabled,
  onFiles,
}: AnnexDropzoneProps) {
  const [dragOver, setDragOver] = useState(false);
  const [validating, setValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = async (candidates: File[]) => {
    if (candidates.length === 0) return;
    setValidating(true);
    const valid: File[] = [];
    for (const candidate of candidates) {
      if (!isXlsx(candidate)) {
        toast.error(`${candidate.name} : seuls les fichiers .xlsx sont acceptés`);
        continue;
      }
      if (candidate.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${candidate.name} : trop volumineux (max ${MAX_SIZE_MB} Mo)`);
        continue;
      }
      const alreadyAdded =
        files.some((f) => f.name === candidate.name) ||
        valid.some((f) => f.name === candidate.name);
      if (alreadyAdded) continue;
      const result = await validateAnnexHeader(candidate, kind);
      if (!result.ok) {
        toast.error(`${candidate.name} : ${result.error ?? 'fichier non reconnu'}`);
        continue;
      }
      valid.push(candidate);
    }
    setValidating(false);
    if (valid.length > 0) onFiles([...files, ...valid]);
  };

  const removeFile = (name: string) =>
    onFiles(files.filter((f) => f.name !== name));

  return (
    <div>
      <Label className="text-sm font-medium text-slate-700">{label}</Label>

      {files.length > 0 && (
        <ul className="mt-1.5 space-y-1.5">
          {files.map((f) => (
            <li
              key={f.name}
              className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700"
            >
              <FileSpreadsheet className="h-4 w-4 shrink-0 text-emerald-600" />
              <span className="truncate font-medium">{f.name}</span>
              <span className="shrink-0 text-slate-400">
                ({formatBytes(f.size)})
              </span>
              <button
                type="button"
                disabled={disabled}
                className="ml-auto shrink-0 text-slate-400 hover:text-rose-500 disabled:opacity-50"
                onClick={() => removeFile(f.name)}
              >
                <X className="h-4 w-4" />
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          if (!disabled && !validating)
            void accept(Array.from(e.dataTransfer.files));
        }}
        onClick={() => !disabled && !validating && inputRef.current?.click()}
        className={cn(
          'mt-1.5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors',
          dragOver
            ? 'border-emerald-400 bg-emerald-50'
            : 'border-slate-200 hover:border-slate-300',
          (disabled || validating) && 'pointer-events-none opacity-60'
        )}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx"
          multiple
          className="hidden"
          onChange={(e) => {
            void accept(Array.from(e.target.files ?? []));
            e.target.value = '';
          }}
        />
        {validating ? (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            Vérification des fichiers…
          </div>
        ) : (
          <>
            <Upload className="h-5 w-5 text-slate-400" />
            <p className="text-sm text-slate-600">
              {files.length > 0
                ? "Ajouter d'autres fichiers"
                : 'Glisse les fichiers ici ou clique pour parcourir'}
            </p>
            <p className="text-xs text-slate-400">{hint}</p>
          </>
        )}
      </div>
    </div>
  );
}

export function DepotForm({ onDone }: { onDone: (runId: string) => void }) {
  const now = new Date();
  const currentYear = now.getFullYear();
  const [billedMonth, setBilledMonth] = useState(now.getMonth() + 1);
  const [billedYear, setBilledYear] = useState(currentYear);
  const [subscriptionFiles, setSubscriptionFiles] = useState<File[]>([]);
  const [consumptionFiles, setConsumptionFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState(0);

  const importRun = useImportBillingRun();
  const intervalRef = useRef<number | null>(null);
  const clearTimer = () => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };
  useEffect(() => clearTimer, []);

  const handleSubmit = () => {
    if (subscriptionFiles.length === 0) {
      toast.error("Au moins une annexe abonnements est requise");
      return;
    }
    setProgress(0);
    clearTimer();
    intervalRef.current = window.setInterval(() => {
      setProgress((p) => (p >= 90 ? p : p + Math.max(0.5, (90 - p) * 0.06)));
    }, 250);
    importRun.mutate(
      { billedMonth, billedYear, subscriptionFiles, consumptionFiles },
      {
        onSuccess: (summary) => {
          clearTimer();
          setProgress(100);
          onDone(summary.billingRunId);
        },
        onError: () => {
          clearTimer();
          setProgress(0);
        },
      }
    );
  };

  const years = Array.from({ length: 6 }, (_, i) => currentYear - 4 + i);
  const isUploading = importRun.isPending;

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle className="text-base">
          Déposer les annexes Sewan du mois
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-sm font-medium text-slate-700">Mois</Label>
            <Select
              value={String(billedMonth)}
              onValueChange={(v) => setBilledMonth(Number(v))}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map((m, i) => (
                  <SelectItem key={m} value={String(i + 1)}>
                    {m}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm font-medium text-slate-700">Année</Label>
            <Select
              value={String(billedYear)}
              onValueChange={(v) => setBilledYear(Number(v))}
            >
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <AnnexDropzone
          label="Annexes abonnements"
          hint="Un ou plusieurs exports Sewan abonnements (.xlsx), même période"
          kind="subscriptions"
          files={subscriptionFiles}
          disabled={isUploading}
          onFiles={setSubscriptionFiles}
        />
        <AnnexDropzone
          label="Annexes consommations"
          hint="Un ou plusieurs exports Sewan consommations (.xlsx, volumineux)"
          kind="consumptions"
          files={consumptionFiles}
          disabled={isUploading}
          onFiles={setConsumptionFiles}
        />

        {(isUploading || progress > 0) && (
          <div className="space-y-1.5">
            <div className="flex justify-between text-xs text-slate-500">
              <span>{isUploading ? 'Analyse en cours…' : 'Terminé'}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress value={progress} />
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={subscriptionFiles.length === 0 || isUploading}
          className="w-full"
        >
          {isUploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Analyse en cours…
            </>
          ) : (
            'Lancer l’analyse'
          )}
        </Button>
      </CardContent>
    </Card>
  );
}

// --- Contrôle (analyzed): summary + per-center table + publish ---------------

export function CentersControl({ runId }: { runId: string }) {
  const { data: run, isLoading } = useBillingRun(runId);
  const publish = usePublishBillingRun();
  const cancelRun = useCancelBillingRun();
  const [confirmCancel, setConfirmCancel] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const setCenter = (siteId: string | null) =>
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (siteId === null) next.delete('center');
        else next.set('center', siteId);
        return next;
      },
      { replace: siteId === null }
    );

  if (isLoading || !run) {
    return <PanelLoader />;
  }

  const resolved = run.centers.filter((c) => c.siteId !== null);
  // The opened center preview lives in the URL (?center=<siteId>) so it is
  // shareable and the browser back button closes the drawer.
  const previewSiteId = searchParams.get('center');
  const previewCenter =
    previewSiteId !== null
      ? (run.centers.find((c) => c.siteId === previewSiteId) ?? null)
      : null;

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            Contrôle — {run.centers.length} centres de facturation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Stat label="Abonnements" value={run.subscriptionCount} />
            <Stat label="Consommations" value={run.consumptionCount} />
            <Stat label="Centres" value={run.centers.length} />
            <Stat
              label="À corriger"
              value={run.unresolvedCount}
              warning={run.unresolvedCount > 0}
            />
          </div>

          <div className="max-h-[420px] overflow-auto rounded-lg border border-slate-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Centre de facturation</TableHead>
                  <TableHead>Entreprise</TableHead>
                  <TableHead className="text-right">Abonnements</TableHead>
                  <TableHead className="text-right">Consommations</TableHead>
                  <TableHead className="text-right">Aperçu</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {run.centers.map((c) => (
                  <TableRow key={c.siteId ?? `name:${c.billingCenterName}`}>
                    <TableCell className="font-medium">
                      {c.billingCenterName || '—'}
                      {c.siteId === null && (
                        <span className="ml-2 text-xs text-amber-600">
                          à corriger
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-slate-500">
                      {c.companyName || '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.subscriptionCount}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.consumptionCount}
                    </TableCell>
                    <TableCell className="text-right">
                      {c.siteId !== null && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCenter(c.siteId!)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between gap-2">
            <p className="text-sm text-slate-500">
              {resolved.length} centre(s) seront publiés dans les Factures.
            </p>
            <div className="flex items-center gap-2">
              {confirmCancel ? (
                <>
                  <span className="text-sm text-slate-500">
                    Supprimer ce run et toutes ses données ?
                  </span>
                  <Button
                    variant="destructive"
                    size="sm"
                    disabled={cancelRun.isPending}
                    onClick={() =>
                      cancelRun.mutate(runId, {
                        onSuccess: () => navigate('/o/admin/billing'),
                      })
                    }
                  >
                    Oui, annuler le run
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setConfirmCancel(false)}
                  >
                    Non
                  </Button>
                </>
              ) : (
                <Button
                  variant="outline"
                  disabled={publish.isPending || cancelRun.isPending}
                  onClick={() => setConfirmCancel(true)}
                >
                  Annuler ce run
                </Button>
              )}
              <Button
                onClick={() =>
                  publish.mutate(runId, {
                    onSuccess: () =>
                      navigate(`/o/admin/billing/${runId}/publication`),
                  })
                }
                disabled={publish.isPending || confirmCancel}
              >
                <Send className="mr-2 h-4 w-4" />
                Publier dans les Factures
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {previewCenter?.siteId != null && (
        <CenterPreviewDrawer
          runId={runId}
          siteId={previewCenter.siteId}
          name={previewCenter.billingCenterName}
          onClose={() => setCenter(null)}
        />
      )}
    </>
  );
}

// --- Publication (publishing/published) --------------------------------------

export function PublicationPanel({ runId }: { runId: string }) {
  const { data: run } = useBillingRunProgress(runId);
  const republish = usePublishBillingRun();
  const cancelRun = useCancelBillingRun();
  const navigate = useNavigate();
  const total = run?.publishTotal ?? 0;
  const done = run?.publishedCount ?? 0;
  const pct = total > 0 ? Math.round((done / total) * 100) : run ? 100 : 0;
  const finished = run?.status === 'published';
  const failed = finished ? Math.max(0, total - done) : 0;

  return (
    <Card className="max-w-2xl">
      <CardHeader>
        <CardTitle className="text-base">
          Publication dans les Factures
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {finished ? (
          <div className="space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-emerald-50 px-4 py-3 text-emerald-700">
              <CheckCircle2 className="h-5 w-5" />
              <span className="text-sm font-medium">
                {done} Détail déposés dans les Factures des clients.
              </span>
            </div>
            {failed > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-lg bg-amber-50 px-4 py-3 text-amber-700">
                <span className="flex items-center gap-2 text-sm">
                  <TriangleAlert className="h-4 w-4" />
                  {failed} fichier(s) en échec.
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  disabled={republish.isPending}
                  onClick={() => republish.mutate(runId)}
                >
                  Reprendre
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Dépôt des Détail en cours…</span>
                <span>
                  {done}/{total || '…'}
                </span>
              </div>
              <Progress value={pct} />
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="ghost"
                disabled={republish.isPending || cancelRun.isPending}
                onClick={() => republish.mutate(runId)}
                className="text-xs text-slate-500"
              >
                La publication semble bloquée ? Reprendre
              </Button>
              <Button
                size="sm"
                variant="ghost"
                disabled={republish.isPending || cancelRun.isPending}
                onClick={() => {
                  if (
                    window.confirm(
                      'Annuler ce run ? Les données importées et les fichiers déjà déposés seront supprimés.',
                    )
                  ) {
                    cancelRun.mutate(runId, {
                      onSuccess: () => navigate('/o/admin/billing'),
                    });
                  }
                }}
                className="text-xs text-red-500 hover:text-red-600"
              >
                ou annuler le run
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// --- Per-center web preview (right drawer) -----------------------------------

const MONTANT_COLUMNS = new Set(['Montant']);
const NUMERIC_COLUMNS = new Set([
  'Montant',
  'Durée (min) / Volume (Mo) / Quantité',
]);

function CenterPreviewDrawer({
  runId,
  siteId,
  name,
  onClose,
}: {
  runId: string;
  siteId: string;
  name: string;
  onClose: () => void;
}) {
  const { data, isLoading } = useBillingCenterPreview(runId, siteId);
  const hiddenConso = data
    ? data.consumptionTotal - data.consumptions.length
    : 0;

  return (
    <Sheet open onOpenChange={(o) => !o && onClose()}>
      <SheetContent
        side="right"
        className="flex w-[92vw] flex-col gap-0 bg-slate-50 p-0 sm:max-w-none"
      >
        <SheetHeader className="shrink-0 gap-0 border-b border-slate-200 bg-white px-8 py-5 pr-14">
          <div className="flex items-center gap-3.5">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-slate-900 text-white">
              <ReceiptEuro className="h-4 w-4" />
            </span>
            <div className="space-y-1">
              <SheetTitle className="text-base leading-none">
                {data?.billingCenterName || name || 'Centre de facturation'}
              </SheetTitle>
              {data && (
                <p className="text-xs leading-none text-slate-500">
                  Détail de facturation · {data.mois}
                </p>
              )}
            </div>
          </div>
        </SheetHeader>

        {isLoading || !data ? (
          <div className="flex flex-1 items-center justify-center">
            <PanelLoader />
          </div>
        ) : (
          <Tabs defaultValue="abo" className="flex min-h-0 flex-1 flex-col gap-0">
            <div className="shrink-0 border-b border-slate-200 bg-white px-8 py-4">
              <TabsList className="bg-slate-100">
                <TabsTrigger value="abo">
                  Abonnements
                  <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                    {data.subscriptions.length}
                  </span>
                </TabsTrigger>
                <TabsTrigger value="conso">
                  Consommations
                  <span className="ml-2 rounded-full bg-slate-200 px-1.5 py-0.5 text-xs text-slate-600">
                    {data.consumptionTotal}
                  </span>
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent
              value="abo"
              className="mt-0 min-h-0 flex-1 overflow-hidden px-8 py-6"
            >
              <RowsTable rows={data.subscriptions} />
            </TabsContent>
            <TabsContent
              value="conso"
              className="mt-0 flex min-h-0 flex-1 flex-col gap-3 overflow-hidden px-8 py-6"
            >
              <RowsTable rows={data.consumptions} />
              {hiddenConso > 0 && (
                <p className="shrink-0 text-xs text-slate-400">
                  … et {hiddenConso} autres lignes (visibles dans l’Excel
                  publié).
                </p>
              )}
            </TabsContent>
          </Tabs>
        )}
      </SheetContent>
    </Sheet>
  );
}

function RowsTable({ rows }: { rows: DetailRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="flex h-full items-center justify-center rounded-xl border border-dashed border-slate-200 bg-white text-sm text-slate-400">
        Aucune ligne pour ce centre.
      </div>
    );
  }
  const columns = Object.keys(rows[0]);
  return (
    <div className="h-full overflow-auto rounded-xl border border-slate-200 bg-white shadow-sm">
      <table className="w-full border-collapse text-sm">
        <thead className="sticky top-0 z-10">
          <tr className="bg-slate-50">
            {columns.map((c) => (
              <th
                key={c}
                className={cn(
                  'whitespace-nowrap border-b border-slate-200 px-4 py-3 text-xs font-semibold uppercase tracking-wide text-slate-500',
                  NUMERIC_COLUMNS.has(c) ? 'text-right' : 'text-left'
                )}
              >
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className="border-b border-slate-100 last:border-0 hover:bg-slate-50/70"
            >
              {columns.map((c) => (
                <td
                  key={c}
                  className={cn(
                    'whitespace-nowrap px-4 py-3 text-slate-700',
                    NUMERIC_COLUMNS.has(c) && 'text-right tabular-nums',
                    MONTANT_COLUMNS.has(c) && 'font-medium text-slate-900'
                  )}
                >
                  {MONTANT_COLUMNS.has(c) && typeof row[c] === 'number'
                    ? `${formatDecimal(row[c] as number)} €`
                    : String(row[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
